import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { FileRestrictions } from '@progress/kendo-angular-upload';
import { SVGIcon, questionCircleIcon } from '@progress/kendo-svg-icons';

import { AuthenticationService } from '../../../auth/authentication.service';
import { CFDocumentToDisplay } from '../../../models/cf-document/cf-document-to-display';
import { TaskToDisplay } from '../../../models/company-tasks/task-to-display';
import { CFEventForCreate } from '../../../models/events/cf-event-for-create';
import { CustomNotificationService } from '../../../services/custom-notification.service';
import { CFDocumentStoreService } from '../../../services/documents/cf-document-store.service';
import { CFEventService } from '../../../services/events/cf-event.service';
import { CustomMessagesService } from '../../../services/translation/custom-messages.service';
import { Constants } from '../../../util/constants';
import { generateUUID } from '../../../util/utils';

@UntilDestroy()
@Component({
    selector: 'clfl-register-event',
    templateUrl: './register-event.component.html',
    styleUrls: ['./register-event.component.scss']
})
export class RegisterEventComponent implements OnInit {
    @Input() isOpened = false;
    @Input() task: TaskToDisplay;

    @Output() public readonly isOpenedChange = new EventEmitter<boolean>();
    @Output() public readonly confirmAction = new EventEmitter<void>();
    @Output() public readonly onLoading = new EventEmitter<boolean>();

    public title!: string;
    public document!: CFDocumentToDisplay;
    public fileRestrictions: FileRestrictions = {
        allowedExtensions: ['.jpg', '.png', '.jpeg', '.pdf'],
        maxFileSize: 10000000
    };

    public questionCircleIcon: SVGIcon = questionCircleIcon;

    public form: FormGroup;

    constructor(
        private msgService: CustomMessagesService,
        private formBuilder: FormBuilder,
        private eventService: CFEventService,
        private notificationService: CustomNotificationService,
        private documentStoreService: CFDocumentStoreService,
        private authenticationService: AuthenticationService
    ) {}

    ngOnInit(): void {
        this.title = this.msgService.translate('cf-company-tasks.dialog.registerEventDialog.title');
        this.form = this.formBuilder.group({
            mention: [null, this.task.needsMention ? Validators.required : Validators.nullValidator],
            files: [null, this.task.needsAttachment ? Validators.required : Validators.nullValidator]
        });

        this.documentStoreService.documentData$.pipe(untilDestroyed(this)).subscribe({
            next: (document) => {
                this.document = document;
            }
        });
    }

    public close(): void {
        this.isOpenedChange.emit(false);
    }

    public save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }

        const eventToRegister = new CFEventForCreate(
            this.task.title,
            Constants.ACTION_EVENT_CODE,
            generateUUID(),
            this.form.value.mention,
            this.document.documentHash,
            this.authenticationService.currentUserValue.userResource.email,
            this.task.status,
            this.task.sendNotification
        );

        let file = undefined;
        if (this.form.value.files && this.form.value.files.length > 0) {
            file = this.form.value.files[0];
        }

        this.onLoading.emit(true);
        this.eventService
            .registerEvent(
                this.documentStoreService.selectedDocumentType,
                this.documentStoreService.companyId,
                this.document.externalId,
                file,
                eventToRegister
            )
            .pipe(untilDestroyed(this))
            .subscribe({
                next: () => {
                    this.notificationService.success(this.msgService.translate('cf-company-tasks.notification.eventRegistered'));
                    this.onLoading.emit(false);
                    this.confirmAction.emit();
                },
                error: () => {
                    this.onLoading.emit(false);
                }
            });
    }
}
